import { observer } from "mobx-react-lite";
import { Chip, Table } from "@heroui/react";
import { SkeletonRow } from "@pro-sto/ui-v2";
import { helpStore } from "@/stores/HelpStore";
import type { HelpOrderListItem } from "@/stores/HelpStore";
import {
  formatHelpDate,
  formatHelpMoney,
  HELP_PAYMENT_LABEL,
  HELP_PROVIDER_LABEL,
  HELP_SERVICE_KIND_COMING_SOON,
  HELP_SERVICE_KIND_LABEL,
  HELP_STATUS_COLOR,
  HELP_STATUS_LABEL,
} from "../constants";

const COLUMN_COUNT = 6;

const HelpTable: React.FC = observer(() => {
  const { visibleItems, isLoading, serviceKind } = helpStore;
  const emptyLabel = HELP_SERVICE_KIND_COMING_SOON[serviceKind]
    ? `${HELP_SERVICE_KIND_LABEL[serviceKind]} — скоро в приложении`
    : "Нет заявок";

  return (
    <Table data-testid="help-table">
      <Table.ScrollContainer>
        <Table.Content
          aria-label="Заявки помощи"
          className="min-w-[920px]"
          selectionMode="none"
          onRowAction={(key) => void helpStore.openOrder(String(key))}
        >
          <Table.Header>
            <Table.Column isRowHeader>Дата</Table.Column>
            <Table.Column>Клиент</Table.Column>
            <Table.Column>Услуга</Table.Column>
            <Table.Column>Статус</Table.Column>
            <Table.Column>Провайдер</Table.Column>
            <Table.Column className="text-right">Сумма</Table.Column>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              Array.from({ length: 8 }, (_, index) => (
                <SkeletonRow
                  key={`skel-${index}`}
                  colCount={COLUMN_COUNT}
                  withCheckbox={false}
                />
              ))
            ) : visibleItems.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={COLUMN_COUNT} className="help-table__empty">
                  {emptyLabel}
                </Table.Cell>
              </Table.Row>
            ) : (
              visibleItems.map((order: HelpOrderListItem) => (
                <Table.Row
                  key={order.uuid}
                  id={order.uuid}
                  className="help-table__row help-table__row--selectable"
                >
                  <Table.Cell>
                    <span title={order.created_at}>
                      {formatHelpDate(order.created_at)}
                    </span>
                  </Table.Cell>
                  <Table.Cell>
                    <div className="help-table__client">
                      <span className="help-table__client-name">
                        {order.client_name || "—"}
                      </span>
                      <span className="help-table__muted">
                        {order.vehicle_reg_number || "—"}
                      </span>
                    </div>
                  </Table.Cell>
                  <Table.Cell>{order.service_title || "—"}</Table.Cell>
                  <Table.Cell>
                    <Chip
                      size="sm"
                      color={HELP_STATUS_COLOR[order.status]}
                      variant="soft"
                    >
                      {HELP_STATUS_LABEL[order.status]}
                    </Chip>
                  </Table.Cell>
                  <Table.Cell>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={order.provider === "lat" ? "accent" : "default"}
                    >
                      {HELP_PROVIDER_LABEL[order.provider]}
                    </Chip>
                  </Table.Cell>
                  <Table.Cell className="text-right">
                    <div className="help-table__pay">
                      <span className="help-table__pay-amount">
                        {formatHelpMoney(order.total_amount)}
                      </span>
                      <span className="help-table__muted">
                        {HELP_PAYMENT_LABEL[order.prepaid_status]}
                      </span>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Content>
      </Table.ScrollContainer>
    </Table>
  );
});

HelpTable.displayName = "HelpTable";
export default HelpTable;
